import { createAsyncThunk } from "@reduxjs/toolkit"

import { errorSave } from "../../../../redux/errors.slice"
import type { BasePagination } from "../../../../types/api.ts"
import { showResponseMessage } from "../../../../utils/handlers/show-response-message.ts"
import * as services from "../../api/services"
import { type TemplateBlockType } from "../../constants/template-block-type.tsx"
import { type DocumentRequestData } from "../../types/document.types.ts"
import type {
  DocumentAvailableBlock,
  DocumentInitialData,
  DocumentTemplate,
  DocumentTemplateFile,
  DocumentTemplateMutateData
} from "../../types/types.ts"
import { documentTemplateSliceName } from "../document-templates.constants.ts"

export const fetchTemplates = createAsyncThunk<
  BasePagination<DocumentTemplate>,
  { page?: number; page_size?: number; q?: string | null } | void
>(
  `${documentTemplateSliceName}/fetchTemplates`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getTemplates(params || undefined)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    return response?.data || { results: [], count: 0 }
  }
)

export const fetchAvailableBlocks = createAsyncThunk<
  {
    all: DocumentAvailableBlock[]
    by_category: Record<TemplateBlockType, DocumentAvailableBlock[]>
  },
  void
>(
  `${documentTemplateSliceName}/fetchAvailableBlocks`,
  async (_, { dispatch, rejectWithValue }) => {
    const response = await services.getAvailableBlocks()

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    return response.data!
  }
)

export const fetchTemplateById = createAsyncThunk<DocumentTemplate, string>(
  `${documentTemplateSliceName}/fetchTemplateById`,
  async (id, { dispatch, rejectWithValue }) => {
    const response = await services.getTemplateById(id)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    return response.data!
  }
)

export const createTemplate = createAsyncThunk<
  DocumentTemplate,
  DocumentTemplateMutateData
>(
  `${documentTemplateSliceName}/createTemplate`,
  async (data, { dispatch, rejectWithValue }) => {
    const response = await services.createTemplate(data)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    showResponseMessage(response, "Template created successfully.")

    return response.data!
  }
)

export const previewTemplate = createAsyncThunk<
  string, // html
  DocumentTemplateMutateData
>(
  `${documentTemplateSliceName}/createTemplate`,
  async (data, { dispatch, rejectWithValue }) => {
    const response = await services.previewTemplate(data)

    if (!response.ok) {
      dispatch(errorSave((response.data as any)?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    return response.data!
  }
)

export const updateTemplate = createAsyncThunk<
  DocumentTemplate,
  { id: string; data: DocumentTemplateMutateData }
>(
  `${documentTemplateSliceName}/updateTemplate`,
  async ({ id, data }, { dispatch, rejectWithValue }) => {
    const response = await services.updateTemplate(id, data)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    showResponseMessage(response, "Template updated successfully.")

    return response.data!
  }
)

export const deleteTemplate = createAsyncThunk<void, string>(
  `${documentTemplateSliceName}/deleteTemplate`,
  async (id, { dispatch, rejectWithValue }) => {
    const response = await services.deleteTemplate(id)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    showResponseMessage(response, "Template deleted successfully.")
  }
)

export const duplicateTemplate = createAsyncThunk<DocumentTemplate, string>(
  `${documentTemplateSliceName}/duplicateTemplate`,
  async (id, { dispatch, rejectWithValue }) => {
    const response = await services.duplicateTemplate(id)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    showResponseMessage(response, "Template duplicated successfully.")

    return response!.data!.details.data
  }
)

// ─── Documents ───
export const fetchDocumentInitialData = createAsyncThunk<
  DocumentInitialData,
  { templateId: string; leadId: string }
>(
  `${documentTemplateSliceName}/fetchDocumentInitialData`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getDocumentInitialData(params)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    return response!.data!.details.data
  }
)

export const fetchDocumentPreview = createAsyncThunk<
  string, // html
  DocumentRequestData & {
    _meta?: Partial<{ doc_type: "contract" }>
  }
>(
  `${documentTemplateSliceName}/fetchDocumentPreview`,
  async (data, { dispatch, rejectWithValue }) => {
    const response = await services.getDocumentPreview(data)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    return response.data!
  }
)

export const uploadAppendixFile = createAsyncThunk<DocumentTemplateFile, File>(
  `${documentTemplateSliceName}/uploadAppendixFile`,
  async (file, { dispatch, rejectWithValue }) => {
    const response = await services.uploadAppendixFile(file)

    if (!response.ok) {
      dispatch(errorSave(response.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    return response!.data!.details.data
  }
)

export const getAppendixFiles = createAsyncThunk<DocumentTemplateFile[], void>(
  `${documentTemplateSliceName}/getAppendixFiles`,
  async (_, { dispatch, rejectWithValue }) => {
    const response = await services.getAppendixFiles()

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    return response.data!
  }
)

export const uploadBlockImage = createAsyncThunk<DocumentTemplateFile, File>(
  `${documentTemplateSliceName}/uploadBlockImage`,
  async (file, { dispatch, rejectWithValue }) => {
    const response = await services.uploadBlockImage(file)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    return response!.data!.details.data
  }
)

export const getBlockImages = createAsyncThunk<DocumentTemplateFile[], void>(
  `${documentTemplateSliceName}/getBlockImages`,
  async (_, { dispatch, rejectWithValue }) => {
    const response = await services.getBlockImages()

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    return response.data!
  }
)
